import React, { useRef, useState } from 'react';
import { Box, Flex, Heading, HStack, Icon, Stack, Text, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { PhoneIcon, MapPinIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { SealButton } from './SealButton';
import { brand, images, whatsappHref } from '../data/brand';
const MotionBox = motion(Box);
const trustItems = [{
  icon: MapPinIcon,
  label: 'Miami, Florida'
}, {
  icon: ShieldCheckIcon,
  label: 'Consultas confidenciales'
}, {
  icon: ClockIcon,
  label: 'Atención por WhatsApp y teléfono'
}];
export function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [glow, setGlow] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = heroRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setGlow({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    });
  };
  return <Box as="section" id="inicio" ref={heroRef} onMouseMove={handleMove} onMouseLeave={() => setGlow(null)} position="relative" bg="ink.900" overflow="hidden" data-id="element-116" data-mp="3j893p1w0vrk9">
      {/* Cursor comet trail */}
      {glow && <MotionBox position="absolute" left={0} top={0} w="22rem" h="22rem" borderRadius="full" pointerEvents="none" zIndex={1} display={{
      base: 'none',
      md: 'block'
    }} bg="radial-gradient(circle, rgba(242,183,5,0.18) 0%, rgba(242,183,5,0.06) 40%, rgba(242,183,5,0) 70%)" animate={{
      x: glow.x - 176,
      y: glow.y - 176
    }} transition={{
      type: 'spring',
      stiffness: 60,
      damping: 18,
      mass: 0.6
    }} data-id="element-117" data-mp="3e9fpt8ctpyjk" />}

      <Flex direction={{
      base: 'column',
      lg: 'row'
    }} align="stretch" position="relative" data-id="element-118" data-mp="2hix03zeukfo0">
        {/* 60% — editorial text column on charcoal with smoke texture */}
        <Box w="100%" position="relative" px={{
        base: 5,
        md: 10,
        xl: 16
      }} pt={{
        base: 16,
        md: 20,
        lg: 28
      }} pb={{
        base: 12,
        lg: 28
      }} zIndex={2} data-id="element-119" data-mp="s21h48i0i7ye">
          <Box position="absolute" inset={0} pointerEvents="none" aria-hidden="true" backgroundImage={`url(${images.hero})`} backgroundSize="cover" backgroundPosition="center" data-id="element-120" data-mp="ibyoi9esyw0l" />
          <Box position="absolute" inset={0} pointerEvents="none" aria-hidden="true" bg="linear-gradient(100deg, rgba(13,13,13,0.9) 0%, rgba(13,13,13,0.8) 55%, rgba(13,13,13,0.7) 100%)" data-id="element-121" data-mp="1ayjilhmnxm7u" />
          <Box position="absolute" inset={0} opacity={0.5} pointerEvents="none" aria-hidden="true" bg="radial-gradient(60% 50% at 20% 25%, rgba(196,18,48,0.22) 0%, rgba(13,13,13,0) 70%), radial-gradient(50% 45% at 75% 80%, rgba(242,183,5,0.12) 0%, rgba(13,13,13,0) 70%)" animation="smokeDriftA 22s ease-in-out infinite" data-id="element-122" data-mp="x0cqyj8b4igm" />

          <VStack align={{
          base: 'flex-start',
          lg: 'center'
        }} textAlign={{
          base: 'left',
          lg: 'center'
        }} spacing={{
          base: 6,
          md: 7
        }} position="relative" maxW={{
          base: '34rem',
          lg: '52rem'
        }} mx="auto" data-id="element-123" data-mp="21bfxpwbcvrxm">
            <HStack spacing={3} data-id="element-124" data-mp="2fz101o0epn4h">
              <Box w="2.5rem" h="1px" bg="gold.400" data-id="element-125" data-mp="2g257qvd2z0zw" />
              <Text fontSize="xs" fontWeight={700} letterSpacing="0.32em" textTransform="uppercase" color="gold.400" data-id="element-126" data-mp="2kxu8v34r0pyb">
                Templo de Ayuda Esotérica
              </Text>
            </HStack>

            <Heading as="h1" fontSize={{
            base: '2.3rem',
            sm: '3rem',
            md: '3.9rem',
            xl: '4.4rem'
          }} color="gold.300" letterSpacing="-0.02em" sx={{
            animation: 'neonFlicker 6s ease-in-out infinite'
          }} data-id="element-127" data-mp="unizrb1m0qku">
              AMARRES DE AMOR Y HECHIZOS MÁGICOS:{' '}
              <Text as="span" color="crimson.500" fontStyle="italic" fontWeight={500} data-id="element-128" data-mp="3vjrre5zkhlpf">
                Orientación Espiritual
              </Text>
            </Heading>

            <Text fontFamily="heading" fontSize={{
            base: 'xl',
            md: '2xl'
          }} fontStyle="italic" color="cream.100" data-id="element-129" data-mp="9bs6117j9po8">
              Nuestros guías espirituales iluminan tu camino.
            </Text>

            <Text fontSize={{
            base: 'md',
            md: 'lg'
          }} color="cream.200" opacity={0.85} data-id="element-130" data-mp="3rglp19fkseo8">
              Somos especialistas en prácticas espirituales, hechizos y amarres tradicionales. A
              través de nuestra experiencia y orientación personalizada, ofrecemos acompañamiento
              para quienes desean explorar diferentes aspectos de su vida personal y sentimental.
            </Text>

            <Stack direction={{
            base: 'column',
            sm: 'row'
          }} spacing={3} align="center" justify="center" alignSelf="center" w="100%" pt={1} data-id="element-131" data-mp="19i5g4n83ngj9">
              <SealButton href={whatsappHref()} external tone="whatsapp" label="Consultar por WhatsApp" caption={brand.whatsapp} icon={<WhatsAppIcon size={19} data-id="element-133" data-mp="1s7c34mfxeuav" />} data-id="element-132" data-mp="zmaqv99den7i" />
              <SealButton href={brand.phoneHref} tone="phone" label="Llamar ahora" caption={brand.phone} ariaLabel={`Llamar al ${brand.phone}`} icon={<PhoneIcon size={18} aria-hidden="true" data-id="element-135" data-mp="1nc51rla4q1p4" />} data-id="element-134" data-mp="1x6x22wv3j2nl" />
            </Stack>

            <Stack direction={{
            base: 'column',
            md: 'row'
          }} spacing={{
            base: 2,
            md: 6
          }} pt={{
            base: 2,
            md: 4
          }} justify={{
            lg: 'center'
          }} data-id="element-136" data-mp="7veesav4wmla">
              {trustItems.map(item => <HStack key={item.label} spacing={2} data-id="element-137" data-mp="29i7j1ypts3wc">
                  <Icon as={item.icon} boxSize="1rem" color="crimson.500" aria-hidden="true" data-id="element-138" data-mp="29ktptw4w6fdi" />
                  <Text fontSize="sm" color="cream.200" opacity={0.8} data-id="element-139" data-mp="24po879iivmv8">
                    {item.label}
                  </Text>
                </HStack>)}
            </Stack>
          </VStack>
        </Box>

      </Flex>
    </Box>;
}